import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import { formatShabbatTimeRule } from '../../shabbatZmanUtils';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const Royal = ({ shabbat, processedCustomTimes, drashaText, loading, error, title = 'זמני שבת', showTitle = true, entryIcon = 'ShabbatCandles', exitIcon = 'MoonStar', dayEntryTime, dayExitTime, bgColor, showTimeRule = false, showEntryExit = true, shabbatLeyning, showLeyning, showHaftarah, leyningDisplayMode }) => {
  const bg = bgColor || '#0d1b2a';
  const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['ShabbatCandles'];
  const ExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['MoonStar'];

  const timeToMinutes = (t) => {
    if (!t || t === '--:--') return -1;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  if (loading) {
    return <div style={{ ...S.wrap, backgroundColor: bg }}><span style={S.stateText}>טוען זמני שבת...</span></div>;
  }
  if (error) {
    return <div style={{ ...S.wrap, backgroundColor: bg }}><span style={{ ...S.stateText, color: '#e57373' }}>{error}</span></div>;
  }
  if (!shabbat) {
    return <div style={{ ...S.wrap, backgroundColor: bg }}><span style={S.stateText}>אין נתוני שבת</span></div>;
  }

  const parasha = shabbat.meta?.parsha || shabbat.parsha || 'פרשת השבוע';
  const entry   = dayEntryTime || shabbat.meta?.candleLighting || shabbat.friday?.candleLighting || '--:--';
  const exit    = dayExitTime  || shabbat.meta?.havdalah      || shabbat.shabbat?.havdalah      || '--:--';

  const fridayTimes  = (processedCustomTimes || []).filter(ct => (ct.day || 'friday') === 'friday').sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  const shabbatTimes = (processedCustomTimes || []).filter(ct => ct.day === 'shabbat').sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  const allCustom    = [...fridayTimes, ...shabbatTimes];

  return (
    <div style={{ ...S.wrap, backgroundColor: bg }} dir="rtl">

      {/* כותרת */}
      {showTitle && (
        <div style={S.header}>
          <EntryIcon style={{ width: '20px', height: '20px', color: GOLD }} />
          <span style={S.headerText}>{title}</span>
          <ExitIcon style={{ width: '20px', height: '20px', color: GOLD }} />
        </div>
      )}

      {/* פרשה */}
      <div style={S.parshaWrap}>
        {shabbat.meta?.parshaShowPrefix !== false && <div style={S.parshaLabel}>פרשת</div>}
        <div style={S.parshaName}>{parasha}</div>
        {shabbat.meta?.parshaSubtitle && <div style={S.parshaSubtitle}>{shabbat.meta.parshaSubtitle}</div>}
        <div style={S.goldLine} />
      </div>

      {/* קריאה */}
      {(showLeyning || showHaftarah) && shabbatLeyning && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '18px',
          borderRadius: '8px',
          border: `1px solid rgba(201,162,39,0.3)`,
          backgroundColor: 'rgba(201,162,39,0.05)',
        }}>
          <LeyningDisplay
            leyning={shabbatLeyning}
            mode={leyningDisplayMode}
            showHaftarah={showHaftarah}
            showMaftir={leyningDisplayMode === 'full'}
            compact={false}
            style={{ color: IVORY }}
          />
        </div>
      )}

      {/* זמני כניסה ויציאה */}
      {showEntryExit && (
        <div style={S.timesGrid}>
          <div style={S.timeCard}>
            <div style={S.timeIconWrap}>
              <EntryIcon style={{ width: '22px', height: '22px', color: bg }} />
            </div>
            <div style={S.timeCardLabel}>כניסת שבת</div>
            <div style={S.timeCardValue}>{entry}</div>
          </div>

          <div style={S.separator} />

          <div style={S.timeCard}>
            <div style={{ ...S.timeIconWrap, backgroundColor: '#1e3a5f' }}>
              <ExitIcon style={{ width: '22px', height: '22px', color: IVORY }} />
            </div>
            <div style={S.timeCardLabel}>צאת שבת</div>
            <div style={S.timeCardValue}>{exit}</div>
          </div>
        </div>
      )}

      {/* דרשה */}
      {drashaText && (
        <div style={S.drashaWrap}>
          <div style={S.drashaInner}>{drashaText}</div>
        </div>
      )}

      {/* זמנים מותאמים */}
      {allCustom.length > 0 && (
        <div style={S.customWrap}>
          {allCustom.map((ct, i) => (
            <div key={i} style={{ ...S.customRow, borderBottom: i < allCustom.length - 1 ? `1px solid rgba(201,162,39,0.2)` : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span style={S.customLabel}>{ct.label}</span>
                {showTimeRule && formatShabbatTimeRule(ct) && (
                  <span style={{ fontSize: '10px', color: MUTED, lineHeight: 1 }}>{formatShabbatTimeRule(ct)}</span>
                )}
              </div>
              <span style={S.customTime}>{ct.time}</span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

const GOLD  = '#C9A227';
const IVORY = '#F5F0E8';
const MUTED = 'rgba(245,240,232,0.55)';

const S = {
  wrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 20px',
    boxSizing: 'border-box',
    gap: '0px',
  },
  stateText: {
    color: IVORY,
    fontSize: '18px',
    margin: 'auto',
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    paddingBottom: '16px',
    borderBottom: `1px solid rgba(201,162,39,0.35)`,
    marginBottom: '18px',
  },
  headerText: {
    fontSize: '17px',
    fontWeight: '600',
    color: GOLD,
    letterSpacing: '0.1em',
  },
  parshaWrap: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  parshaLabel: {
    fontSize: '12px',
    color: MUTED,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  parshaName: {
    fontSize: '38px',
    fontWeight: '800',
    color: GOLD,
    lineHeight: 1.15,
    marginBottom: '12px',
    textShadow: '0 0 20px rgba(201,162,39,0.3)',
  },
  parshaSubtitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: IVORY,
    marginTop: '-4px',
    marginBottom: '8px',
  },
  goldLine: {
    width: '60px',
    height: '2px',
    backgroundColor: GOLD,
    margin: '0 auto',
    opacity: 0.7,
  },
  timesGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '18px',
  },
  timeCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 10px',
    border: `1px solid rgba(201,162,39,0.4)`,
    borderRadius: '10px',
    backgroundColor: 'rgba(201,162,39,0.07)',
  },
  timeIconWrap: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: GOLD,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeCardLabel: {
    fontSize: '13px',
    color: MUTED,
    fontWeight: '600',
    letterSpacing: '0.05em',
  },
  timeCardValue: {
    fontSize: '40px',
    fontWeight: '800',
    color: IVORY,
    direction: 'ltr',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  separator: {
    width: '1px',
    height: '80px',
    backgroundColor: `rgba(201,162,39,0.3)`,
    flexShrink: 0,
  },
  drashaWrap: {
    marginBottom: '14px',
    padding: '12px 16px',
    border: `1px solid rgba(201,162,39,0.3)`,
    borderRadius: '8px',
    backgroundColor: 'rgba(201,162,39,0.05)',
    textAlign: 'center',
  },
  drashaInner: {
    fontSize: '16px',
    color: IVORY,
    fontWeight: '600',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
  },
  customWrap: {
    borderRadius: '8px',
    border: `1px solid rgba(201,162,39,0.25)`,
    backgroundColor: 'rgba(255,255,255,0.03)',
    overflow: 'hidden',
  },
  customRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
  },
  customLabel: {
    fontSize: '15px',
    color: MUTED,
    fontWeight: '500',
  },
  customTime: {
    fontSize: '18px',
    fontWeight: '700',
    color: GOLD,
    direction: 'ltr',
  },
};

export default Royal;
